import React, { useEffect, useState } from 'react';
import { db, storage } from '../../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, Timestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Pencil, Trash2, PlusCircle, UploadCloud } from 'lucide-react';

const defaultForm = {
  date: '',
  heure: '',
  adversaire: '',
  lieu: '',
  statut: 'à venir',
  score: '',
  equipe: 'N2',
  logo: '',
};

const Matches = () => {
  const [matchs, setMatchs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'matchs'), orderBy('date', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setMatchs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = e => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  const uploadLogo = async (file, adversaire, matchId = null) => {
    if (!file) return '';
    const ext = file.name.split('.').pop();
    const safeName = adversaire.replace(/[^a-zA-Z0-9]/g, '_');
    const path = `logos_matchs/${safeName}_${Date.now()}${matchId ? '_' + matchId : ''}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleAddOrEditMatch = async (e) => {
    e.preventDefault();
    setAdding(true);
    setFeedback('');
    try {
      // Création de la date complète
      const dateObj = new Date(`${form.date}T${form.heure || '00:00'}`);
      let logoUrl = form.logo;
      if (logoFile) {
        logoUrl = await uploadLogo(logoFile, form.adversaire, editId);
      }
      if (editId) {
        // Edition
        await updateDoc(doc(db, 'matchs', editId), {
          date: Timestamp.fromDate(dateObj),
          adversaire: form.adversaire,
          lieu: form.lieu,
          statut: form.statut,
          score: form.score,
          equipe: form.equipe,
          logo: logoUrl,
        });
        setFeedback('Match modifié avec succès !');
      } else {
        // Ajout
        await addDoc(collection(db, 'matchs'), {
          date: Timestamp.fromDate(dateObj),
          adversaire: form.adversaire,
          lieu: form.lieu,
          statut: form.statut,
          score: form.score,
          equipe: form.equipe,
          logo: logoUrl,
          createdAt: Timestamp.now(),
        });
        setFeedback('Match ajouté avec succès !');
      }
      setShowModal(false);
      setForm(defaultForm);
      setLogoFile(null);
      setEditId(null);
    } catch (err) {
      setFeedback("Erreur : " + err.message);
    }
    setAdding(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  const handleEdit = (match) => {
    setForm({
      date: match.date ? new Date(match.date.seconds * 1000).toISOString().slice(0, 10) : '',
      heure: match.date ? new Date(match.date.seconds * 1000).toISOString().slice(11, 16) : '',
      adversaire: match.adversaire || '',
      lieu: match.lieu || '',
      statut: match.statut || 'à venir',
      score: match.score || '',
      equipe: match.equipe || 'N2',
      logo: match.logo || '',
    });
    setEditId(match.id);
    setShowModal(true);
    setLogoFile(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      // Supprimer le logo du storage si existant
      if (toDelete.logo && toDelete.logo.includes('logos_matchs')) {
        try {
          const logoRef = ref(storage, toDelete.logo);
          await deleteObject(logoRef);
        } catch (e) { /* ignore */ }
      }
      await deleteDoc(doc(db, 'matchs', toDelete.id));
      setFeedback('Match supprimé.');
    } catch (err) {
      setFeedback("Erreur lors de la suppression : " + err.message);
    }
    setShowConfirm(false);
    setToDelete(null);
    setTimeout(() => setFeedback(''), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gestion des Matchs</h1>
      <button
        className="flex items-center gap-2 bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold px-4 py-2 rounded-xl mb-6 shadow transition"
        onClick={() => { setShowModal(true); setEditId(null); setForm(defaultForm); setLogoFile(null); }}
      >
        <PlusCircle size={20} /> Ajouter un match
      </button>
      {feedback && <div className="mb-4 text-white font-semibold">{feedback}</div>}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleAddOrEditMatch} className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-lg flex flex-col gap-4 animate-fade-in">
            <h2 className="text-xl font-bold mb-2">{editId ? 'Modifier le match' : 'Ajouter un match'}</h2>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block mb-1">Date</label>
                <input type="date" name="date" value={form.date} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div className="flex-1">
                <label className="block mb-1">Heure</label>
                <input type="time" name="heure" value={form.heure} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
            </div>
            <div>
              <label className="block mb-1">Adversaire</label>
              <input type="text" name="adversaire" value={form.adversaire} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
            </div>
            <div>
              <label className="block mb-1">Lieu</label>
              <input type="text" name="lieu" value={form.lieu} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block mb-1">Statut</label>
                <select name="statut" value={form.statut} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
                  <option value="à venir">À venir</option>
                  <option value="joué">Joué</option>
                  <option value="reporté">Reporté</option>
                  <option value="annulé">Annulé</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block mb-1">Score</label>
                <input type="text" name="score" value={form.score} onChange={handleChange} placeholder="ex: 2-1" className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block mb-1">Équipe</label>
                <select name="equipe" value={form.equipe} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
                  <option value="SENIORS N2">SENIORS N2</option>
                  <option value="SENIORS R1">SENIORS R1</option>
                  <option value="U18 R1">U18 R1</option>
                  <option value="U17 R2">U17 R2</option>
                  <option value="U16 R1">U16 R1</option>
                  <option value="U15 R1">U15 R1</option>
                  <option value="U14 Ligue">U14 Ligue</option>
                  <option value="U18F R2">U18F R2</option>
                  <option value="U16 D2">U16 D2</option>
                  <option value="U14 D1">U14 D1</option>
                  <option value="U13 Niveau A">U13 Niveau A</option>
                  <option value="U12 Niveau A">U12 Niveau A</option>
                  <option value="U11 Niveau A">U11 Niveau A</option>
                  <option value="U10 Niveau A">U10 Niveau A</option>

                </select>
              </div>
              <div className="flex-1">
                <label className="block mb-1">Logo adversaire</label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="w-full text-white" />
                  {form.logo && !logoFile && (
                    <img src={form.logo} alt="logo" className="w-8 h-8 rounded-full bg-white" />
                  )}
                  {logoFile && (
                    <UploadCloud size={22} className="text-[#FFE400] animate-bounce" />
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-2">
              <button type="button" className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowModal(false); setEditId(null); setLogoFile(null); }} disabled={adding}>Annuler</button>
              <button type="submit" className="px-6 py-2 rounded bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold" disabled={adding}>{adding ? (editId ? 'Modification...' : 'Ajout...') : (editId ? 'Modifier' : 'Ajouter')}</button>
            </div>
          </form>
        </div>
      )}
      {/* Confirmation suppression */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 animate-fade-in">
            <h2 className="text-xl font-bold">Confirmer la suppression</h2>
            <p>Voulez-vous vraiment supprimer ce match ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowConfirm(false); setToDelete(null); }}>Annuler</button>
              <button className="px-6 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-white">Chargement...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-[#00238D] rounded-xl shadow-lg">
            <thead>
              <tr className="text-[#FFE400] text-left">
                <th className="py-2 px-4">Date</th>
                <th className="py-2 px-4">Adversaire</th>
                <th className="py-2 px-4">Lieu</th>
                <th className="py-2 px-4">Score</th>
                <th className="py-2 px-4">Statut</th>
                <th className="py-2 px-4">Équipe</th>
                <th className="py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {matchs.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-white py-8">Aucun match enregistré.</td></tr>
              ) : (
                matchs.map(match => (
                  <tr key={match.id} className="border-b border-[#001B56] hover:bg-[#001B56]/40 transition">
                    <td className="py-2 px-4">{match.date ? new Date(match.date.seconds ? match.date.seconds * 1000 : match.date).toLocaleDateString() : '-'}</td>
                    <td className="py-2 px-4 flex items-center gap-2">
                      {match.logo && <img src={match.logo} alt="logo adv" className="w-6 h-6 rounded-full bg-white" />}
                      {match.adversaire || '-'}
                    </td>
                    <td className="py-2 px-4">{match.lieu || '-'}</td>
                    <td className="py-2 px-4">{match.score || '-'}</td>
                    <td className="py-2 px-4">{match.statut || '-'}</td>
                    <td className="py-2 px-4">{match.equipe || '-'}</td>
                    <td className="py-2 px-4 flex gap-2">
                      <button className="p-2 rounded hover:bg-[#FFE400]/20 transition" title="Éditer" onClick={() => handleEdit(match)}><Pencil size={18} /></button>
                      <button className="p-2 rounded hover:bg-red-500/30 transition" title="Supprimer" onClick={() => { setShowConfirm(true); setToDelete(match); }}><Trash2 size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Matches; 